import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M11 L11.11 — Quality Score Screen.
 * Displays 6-category quality assessment with weighted scoring.
 */
interface QualityScore {
  readonly verificationId: string;
  readonly overallScore: number | null;
  readonly categories: QualityCategory[];
  readonly gradedBy: string | null;
  readonly gradedAt: string | null;
}

interface QualityCategory {
  readonly name: string;
  readonly score: number;
  readonly weight: number;
  readonly weightedScore: number;
  readonly remarks: string | null;
}

const CATEGORIES = [
  'Photo Compliance', 'Brand Visibility', 'GPS Accuracy',
  'Area Accuracy', 'Execution Quality', 'Overall Finish',
];

export function QualityScorePage(): ReactNode {
  const { t } = useTranslation();
  const { number, decimal } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const { verificationId } = useParams<{ verificationId: string }>();
  const auth = useAuth();
  const [data, setData] = useState<QualityScore | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<QualityScore>(`/v1/closure/review/${verificationId}/quality`, { token: auth.token })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load quality score'); });
    return () => { alive = false; };
  }, [auth.token, verificationId]);

  if (error) return <main aria-label="quality-score"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!data) return <main aria-label="quality-score"><p>{t('closure.quality.loading')}</p></main>;

  const categories = data.categories.length > 0
    ? data.categories
    : CATEGORIES.map((n) => ({ name: n, score: 0, weight: 1, weightedScore: 0, remarks: null }));

  const scoreColor = (s: number | null) => s == null ? 'var(--color-text-muted)' : s >= 80 ? 'var(--color-success)' : s >= 50 ? 'var(--color-warning)' : 'var(--color-danger)';

  return (
    <main aria-label="quality-score">
      <h2>{t('closure.quality.title')}</h2>
      <p><Link to={`/closure/final/review/${verificationId}`}>{t('closure.quality.back')}</Link></p>

      <div style={{ marginBottom: 20, padding: 20, backgroundColor: 'var(--color-info-bg)', borderRadius: 8, textAlign: 'center' }}>
        <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>{t('closure.quality.overall')}</div>
        <div style={{ fontSize: 48, fontWeight: 'bold', color: scoreColor(data.overallScore) }}>
          {decimal(data.overallScore)}
        </div>
        <div style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>/ 100</div>
        {data.gradedBy && (
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 8 }}>
            {t('closure.quality.gradedBy')} {data.gradedBy} | {atDay(data.gradedAt)}
          </div>
        )}
      </div>

      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 700 }}>
        <thead>
          <tr>
            <th style={th}>{t('closure.quality.col.category')}</th>
            <th style={{ ...th, textAlign: 'center' }}>{t('closure.quality.col.score')}</th>
            <th style={{ ...th, textAlign: 'center' }}>{t('closure.quality.col.weight')}</th>
            <th style={{ ...th, textAlign: 'center' }}>{t('closure.quality.col.weighted')}</th>
            <th style={th}>{t('closure.quality.col.remarks')}</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((c) => (
            <tr key={c.name} data-testid={`quality-${c.name.replace(/\s/g, '-').toLowerCase()}`}>
              <td style={td}>{c.name}</td>
              <td style={{ ...td, textAlign: 'center', color: scoreColor(c.score) }}>{number(c.score)}</td>
              <td style={{ ...td, textAlign: 'center' }}>{number(c.weight)}</td>
              <td style={{ ...td, textAlign: 'center', fontWeight: 'bold' }}>{number(c.weightedScore, 1)}</td>
              <td style={td}>{c.remarks ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--color-border-default)' };
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--color-border-default)' };
